import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ModalController, Platform } from '@ionic/angular';
import { Customer } from '@usf/customer-types';
import {
  MyOrdersStoreService,
  Order,
  OrderState,
  OrderStatus,
  OrderReviewStoreService,
} from '@usf/ngrx-order';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, skip, take, withLatestFrom } from 'rxjs/operators';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from 'src/app/shared/constants/page-titles.enum';
import {
  MyOrdersQuickFilters,
  MyOrdersViewModel,
} from 'src/app/shared/models/my-orders-view.model';
import { OrderService } from '../../../order/services/order.service';
import { ModalWithNavComponent } from '../../../shared/components/modal-with-nav/modal-with-nav.component';
import {
  DeliveryMethod,
  DeliveryMethodDetails,
  OrderStatusDetails,
} from '../../../shared/constants/order-status-title.enum';
import { MyOrdersFilterAnalyticsService } from '../../services/my-orders-filter-analytics.service';
import { MyOrdersService } from '../../services/my-orders.service';
import { PATHS } from '../../../shared/constants/paths';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { HelpModalDesktopComponent } from '../../../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../../../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { QuickFilter } from '../../../shared/components/usf-filtering-banner/model/quick-filter.model';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { MyOrdersFiltersDesktopTabletComponent } from '@app/my-orders/my-orders-filters/my-orders-filters-desktop-tablet/my-orders-filters-desktop-tablet.component';
import { MyOrdersFiltersMobileComponent } from '@app/my-orders/my-orders-filters/my-orders-filters-mobile/my-orders-filters-mobile.component';
import { TranslateService } from '@ngx-translate/core';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { DownloadOrdersEvent } from '@order/models/download-orders-event';
import { FEATURES } from '@shared/constants/splitio-features';
import { OrderStatusPageService } from './order-status-page.service';
import { OrderStatusFilters } from '@usf/user-types/user-preferencev2';
import { UserService } from '@app/user/services';

@Component({
  selector: 'app-order-status',
  templateUrl: './order-status.page.html',
  styleUrls: ['./order-status.page.scss'],
})
export class OrderStatusPage implements OnInit {
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('mobileVirtualScroll', { static: false })
  mobileVirtualScroll: CdkVirtualScrollViewport;
  @ViewChild('tabletVirtualScroll', { static: false })
  tabletVirtualScroll: CdkVirtualScrollViewport;

  breadcrumbs: Crumb[];
  customers: Customer[] = [];
  ordersListSubscription$: Subscription;
  reviewedOrdersLoadingSubscription$: Subscription;
  myOrders: MyOrdersViewModel;
  modalOpen = false;
  numberOfSelectedFilters = 0;
  formObject: UntypedFormGroup;
  @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;
  orderStatusEnum = OrderStatus;
  deliveryDateRange = 'Select a date range';
  numberOfOrders: number;
  checkNumberOrders: number;
  hideRefresher = false;
  orderReviewLoading = false;
  isIOS = false;

  orders$: Observable<Order[]>;
  downloadOrdersModalIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadOrdersActionSheetIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadOrdersFileTypes = [DownloadFileTypes.CSV];
  downloadOrdersActionSheetButtons = [
    { text: 'Download', handler: () => this.showDownloadOrdersModal(true) },
    { text: 'Cancel', role: 'cancel', cssClass: 'cancel-button' },
  ];
  today = new Date();
  FEATURES = FEATURES;

  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private myOrdersStoreService: MyOrdersStoreService,
    private orderService: OrderService,
    private router: Router,
    private modalController: ModalController,
    private formBuilder: UntypedFormBuilder,
    private myOrdersService: MyOrdersService,
    private cdRef: ChangeDetectorRef,
    private modalService: ModalService,
    private translateService: TranslateService,
    private orderStatusPageService: OrderStatusPageService,
    private orderReviewStoreService: OrderReviewStoreService,
    private readonly userService: UserService,
    private platform: Platform,
  ) {
    this.isIOS = this.platform.is('ios');
  }

  ngOnInit() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.orderStatus',
        url: '',
        active: true,
      },
    ];
  }

  reloadList() {
    if (this.viewPort) {
      setTimeout(() => {
        //this.viewPort.checkViewportSize();
        this.cdRef.detectChanges();
      });
    }
  }

  ionViewWillEnter() {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.myOrders),
    );

    // Initialize viewport and load data immediately, without setTimeout
    this.myOrdersStoreService.retrieveRecentOrders();

    // first event emitted create the form, load filters and update orders
    this.orderService
      .loadMyOrdersViewModel()
      .pipe(
        withLatestFrom(
          this.panAppState.user$,
          this.userService
            .getUserPreferencesV2IsLoading()
            .pipe(filter(prefsLoading => !prefsLoading)),
        ),
        filter(([x, user]) => x.customers?.length > 0 && !!user),
        map(([x, user]) => x),
        take(2),
      )
      .subscribe(x => {
        this.customers = x.customers;
        this.myOrders = x;
        this.reloadList();

        // Ensure change detection runs after data is loaded
        this.cdRef.detectChanges();

        if (
          !!x.filters?.filters?.deliveryDate?.start ||
          !!x.filters?.filters?.deliveryDate?.end
        ) {
          this.deliveryDateRange = `${x.filters?.filters?.deliveryDate?.start} - ${x?.filters?.filters?.deliveryDate?.end}`;
        }
        if (!this.formObject && !this.originalFilters(x.filters)) {
          this.myOrdersStoreService.setMyOrdersFilters(x.defaultDateFilter, 1);
          this.createForm(x.defaultDateFilter);
          this.numberOfSelectedFilters = 1;
        } else if (x.filters && x.filters.filterSelected) {
          this.createForm();
          this.formObject.patchValue(x.filters.filters);
          this.updateFilterCount();
        }

        // Run change detection again after form is updated
        this.cdRef.detectChanges();
      });

    // after first event emitted only update orders
    this.ordersListSubscription$ = this.orderService
      .loadMyOrdersViewModel()
      .pipe(
        withLatestFrom(
          this.panAppState.user$,
          this.userService
            .getUserPreferencesV2IsLoading()
            .pipe(filter(prefsLoading => !prefsLoading)),
        ),
        filter(([x, user]) => x.customers?.length > 0 && !!user),
        map(([x, user]) => x),
        skip(1),
      )
      .subscribe(x => {
        if (this.customers.length === 0) {
          this.customers = x.customers;
          this.createCustomersForm();
        }
        this.myOrders = x;
        this.cdRef.detectChanges();
      });

    this.reviewedOrdersLoadingSubscription$ =
      this.orderReviewStoreService.loading$.pipe().subscribe(loading => {
        this.orderReviewLoading = loading;
      });

    if (this.userService.isInternalUser && !this.orderReviewLoading) {
      this.orderReviewStoreService.getOrderReviewPreferences();
    }

    this.orders$ = this.orderService.loadOrderStatusOrders$();
  }

  ionViewDidEnter() {
    // First handle the viewport size
    window.dispatchEvent(new Event('resize'));

    // Make sure we have a stable virtual scroll view before loading data
    this.ensureViewportsInitialized();

    // Add a small delay to ensure the height calculation happens in the next change detection cycle
    setTimeout(() => {
      this.cdRef.detectChanges();

      let viewFilters: any;
      const myOrdersFilters: string[] = [];

      this.orderService
        .loadMyOrdersViewModel()
        .pipe(
          withLatestFrom(
            this.panAppState.user$,
            this.userService
              .getUserPreferencesV2IsLoading()
              .pipe(filter(prefsLoading => !prefsLoading)),
          ),
          filter(([x, user]) => x.customers?.length > 0 && !!user),
          map(([x, user]) => x),
          take(1),
        )
        .subscribe(vm => {
          viewFilters = vm.filters.filters;

          if (viewFilters) {
            const orderStatuses =
              MyOrdersFilterAnalyticsService.orderStatuses(viewFilters);
            const customerIds =
              MyOrdersFilterAnalyticsService.distinctCustomerIds(viewFilters);
            const deliveryDates =
              MyOrdersFilterAnalyticsService.deliveryDate(viewFilters);
            if (!!orderStatuses?.length) {
              myOrdersFilters.push(`orderStatus=${orderStatuses}`);
            }
            if (!!customerIds?.length) {
              myOrdersFilters.push(`customerID=${customerIds}`);
            }
            if (!!deliveryDates?.length) {
              myOrdersFilters.push(`deliveryDate=${deliveryDates}`);
            }
          }

          this.myOrdersService.trackPageLoad(myOrdersFilters.join(';'));
          this.enableRefresher();

          // Final change detection after all analytics are set
          this.cdRef.detectChanges();
        });
    });
  }

  ionViewWillLeave() {
    if (this.ordersListSubscription$) {
      this.ordersListSubscription$.unsubscribe();
    }
    if (this.reviewedOrdersLoadingSubscription$) {
      this.reviewedOrdersLoadingSubscription$.unsubscribe();
    }
  }

  createNewOrder(): void {
    this.orderService.createNewOrder();
    this.routeTo(PATHS.LISTS);
  }

  routeTo(path: string) {
    this.router.navigate([path]);
  }

  quickFitlerCanBeActivated(filters: OrderStatus[]): boolean {
    let quickFitlersCanBeActivated = false;
    const allFiltersSelected = filters.every(
      statusKey => this.formObject?.value?.orderStatus[statusKey],
    );
    if (!allFiltersSelected) {
      quickFitlersCanBeActivated = true;
    } else {
      Object.keys(this.formObject.value.orderStatus).every(key => {
        if (!filters.includes(OrderStatus[key])) {
          if (this.formObject.value.orderStatus[key]) {
            quickFitlersCanBeActivated = true;
            return false;
          }
        }
        return true;
      });
    }

    return quickFitlersCanBeActivated;
  }

  deactivateStatusFilters(filters: OrderStatus[]) {
    let newFormObjectValue = { ...this.formObject.value };
    filters.forEach(filter => {
      newFormObjectValue = {
        ...newFormObjectValue,
        orderStatus: {
          ...newFormObjectValue.orderStatus,
          [filter]: false,
        },
      };
    });
    return newFormObjectValue;
  }

  activateStatusFilters(filters: OrderStatus[]) {
    let newOrderStatusFilters = new Map<OrderStatus, boolean>();
    Object.keys(this.formObject.value.orderStatus).forEach(statusKey => {
      if (filters.includes(OrderStatus[statusKey])) {
        newOrderStatusFilters[statusKey] = true;
      } else {
        newOrderStatusFilters[statusKey] = false;
      }
    });
    const newFormObjectValue = {
      ...this.formObject.value,
      orderStatus: newOrderStatusFilters,
    };
    return newFormObjectValue;
  }

  toggleStatusFilter(quickFilter: QuickFilter) {
    let newFormObjectValue;
    if (quickFilter.id === MyOrdersQuickFilters.OPEN_ORDERS) {
      if (
        this.quickFitlerCanBeActivated([
          OrderStatus.NO_ORDER,
          OrderStatus.IN_PROGRESS,
        ])
      ) {
        newFormObjectValue = this.activateStatusFilters([
          OrderStatus.NO_ORDER,
          OrderStatus.IN_PROGRESS,
        ]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([
          OrderStatus.NO_ORDER,
          OrderStatus.IN_PROGRESS,
        ]);
      }
    } else if (quickFilter.id === MyOrdersQuickFilters.MY_DELIVERIES) {
      if (
        this.quickFitlerCanBeActivated([
          OrderStatus.DELIVERED,
          OrderStatus.SHIPPED,
        ])
      ) {
        newFormObjectValue = this.activateStatusFilters([
          OrderStatus.DELIVERED,
          OrderStatus.SHIPPED,
        ]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([
          OrderStatus.DELIVERED,
          OrderStatus.SHIPPED,
        ]);
      }
    } else {
      if (this.quickFitlerCanBeActivated([OrderStatus[quickFilter.id]])) {
        newFormObjectValue = this.activateStatusFilters([quickFilter.id]);
      } else {
        newFormObjectValue = this.deactivateStatusFilters([quickFilter.id]);
      }
    }

    this.formObject.patchValue(newFormObjectValue);
    this.updateFilterCount();
    this.applyFilters(
      { data: { numberOfSelectedFilters: this.numberOfSelectedFilters } },
      newFormObjectValue,
    );
    const filtersForTracking =
      this.formatQuickFiltersForTracking(newFormObjectValue);
    this.myOrdersService.trackQuickFilterClick(filtersForTracking);
  }

  formatQuickFiltersForTracking(filters): string {
    let orderStatusFilters: string[] = [];
    const orderStatuses = MyOrdersFilterAnalyticsService.orderStatuses(filters);
    const deliveryDates = MyOrdersFilterAnalyticsService.deliveryDate(filters);
    if (!!orderStatuses?.length) {
      orderStatusFilters.push(`preset=${orderStatuses}`);
    }
    if (!!deliveryDates?.length) {
      orderStatusFilters.push(`deliveryDate=${deliveryDates}`);
    }
    if (!!orderStatuses?.length) {
      orderStatusFilters.push(`orderStatus=${orderStatuses}`);
    }
    return orderStatusFilters.join(';');
  }

  statusFilters(): Map<OrderStatus, string> {
    const filterStatusText: Map<OrderStatus, string> = new Map();
    Object.keys(OrderStatus).forEach(status => {
      if (
        status === OrderStatus.QUANTITIES_UPDATED ||
        status === OrderStatus.OUT_FOR_DELIVERY ||
        status === OrderStatus.DELIVERY_DELAYED ||
        status === OrderStatus.ATTEMPTED_DELIVERY ||
        status === OrderStatus.ORDER_CANCELLED ||
        status === OrderStatus.DELIVERY_ISSUE ||
        status === OrderStatus.PREPARING_TO_SHIP
      ) {
        return;
      }
      const text = OrderStatusDetails[status]?.filterText;
      if (!!text) {
        filterStatusText.set(
          OrderStatus[status],
          this.translateService.instant(OrderStatusDetails[status]?.filterText),
        );
      }
    });
    return filterStatusText;
  }

  deliveryMethodFilters(): Map<DeliveryMethod, string> {
    const filterMethodText: Map<DeliveryMethod, string> = new Map();
    Object.keys(DeliveryMethod).forEach(method => {
      const text = DeliveryMethodDetails[method]?.filterText;
      if (!!text) {
        filterMethodText.set(
          DeliveryMethod[method],
          this.translateService.instant(
            DeliveryMethodDetails[method]?.filterText,
          ),
        );
      }
    });
    return filterMethodText;
  }

  sellerFilters(): Map<string, string> {
    // Mock implementation - replace with actual seller data in production
    const filterSellerText: Map<string, string> = new Map();
    filterSellerText.set('SELLER_1', 'Seller 1');
    filterSellerText.set('SELLER_2', 'Seller 2');
    filterSellerText.set('SELLER_3', 'Seller 3');
    filterSellerText.set('SELLER_1_CUSTOMER', 'Seller 1 Customer');
    return filterSellerText;
  }

  // check if any filter is selected... maybe the modal could return a variable that track that. I would prevent the looping into

  async openFilterModal(quickDate?: boolean) {
    const filters = {
      orderStatus: {
        displayName: 'i18n.orderStatusPage.orderStatus',
        value: this.statusFilters(),
      },
      customers: {
        displayName: 'i18n.common.customers',
        value: this.customers,
        searchable: true,
      },
      deliveryDate: {
        displayName: 'i18n.orderStatusPage.deliveryDate',
        value: [],
      },
      deliveryMethod: {
        displayName: 'i18n.orderStatusPage.deliveryMethod',
        value: this.deliveryMethodFilters(),
      },
    };

    // Add seller filter only if the feature is enabled
    if (FEATURES.split_dynamic_order_status_seller_filter.control) {
      filters['seller'] = {
        displayName: 'i18n.orderStatusPage.seller',
        value: this.sellerFilters(),
      };
    }

    if (!this.modalOpen) {
      this.modalOpen = true;

      if (!this.formObject) {
        this.createForm();
      }

      // Explicitly clear the preset value before opening the modal
      this.formObject.get('deliveryDate').patchValue({ preset: null });
      this.formObject.get('rememberDeliveryDatePreset').patchValue(false);

      const rootPageParams = {
        filters,
        filtersFormGroup: this.formObject,
      };
      const rootPageParamsQuickDate = {
        filters,
        filtersFormGroup: this.formObject,
        selectedFilter: 'deliveryDate',
        quickDate: true,
      };
      const formValue = this.formObject.value;
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        ModalWithNavComponent,
        {
          rootPage:
            this.platformService.platformType ===
            this.platformService.platformEnum.mobile
              ? MyOrdersFiltersMobileComponent
              : MyOrdersFiltersDesktopTabletComponent,
          rootPageParams: quickDate ? rootPageParamsQuickDate : rootPageParams,
        },
        'modal-desktop-size-xl',
      );
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then((resp: any) => {
        this.applyFilters(resp, formValue);
      });
      await modal.present();
    }
  }

  applyFilters(resp, formValue) {
    if (resp.data) {
      this.numberOfSelectedFilters = resp.data.numberOfSelectedFilters;
      // The formValue object contains the filters of the ordersFilters object
      const {
        rememberOrderStatus,
        rememberCustomers,
        rememberDeliveryMethod,
        rememberDeliveryDatePreset,
        ...filterValues
      } = this.formObject.value;

      // Create the orderFilters object
      const orderStatusFilters: OrderStatusFilters = {
        filterSelected: this.numberOfSelectedFilters,
        filters: filterValues,
      };

      // Check if any category's remember selection is enabled
      const rememberSelections = resp.data.rememberSelections || {
        orderStatus: false,
        customers: false,
        deliveryMethod: false,
        deliveryDatePreset: false,
      };

      const shouldSavePreferences =
        rememberSelections.orderStatus ||
        rememberSelections.customers ||
        rememberSelections.deliveryMethod ||
        rememberSelections.deliveryDatePreset;

      if (shouldSavePreferences) {
        this.orderStatusPageService.saveFilterPreferences(
          orderStatusFilters,
          rememberSelections,
        );
      }
      this.myOrdersStoreService.setMyOrdersFilters(
        filterValues,
        this.numberOfSelectedFilters,
      );

      // Run change detection immediately instead of using setTimeout
      this.cdRef.detectChanges();
    } else {
      // If modal was dismissed without applying filters, keep the current form values
      this.formObject.patchValue(formValue);
    }
    this.modalOpen = false;
    this.updateFilterCount();

    if (
      this.deliveryDateRange === 'null - null' ||
      this.deliveryDateRange === ' - '
    ) {
      this.deliveryDateRange = 'Select a date range';
    }
  }

  updateFilterCount() {
    let newFilterCount = 0;
    Object.keys(this.formObject.value.orderStatus).forEach(key => {
      if (this.formObject.value.orderStatus[key]) {
        newFilterCount++;
      }
    });
    Object.keys(this.formObject.value.customers).forEach(key => {
      if (typeof this.formObject.value.customers[key] === 'object') {
        Object.keys(this.formObject.value.customers[key]).forEach(
          department => {
            if (this.formObject.value.customers[key][department] === true) {
              newFilterCount++;
            }
          },
        );
      } else if (this.formObject.value.customers[key] === true) {
        newFilterCount++;
      }
    });

    if (
      this.formObject.value.deliveryDate.start &&
      this.formObject.value.deliveryDate.end
    ) {
      newFilterCount++;
    }

    Object.keys(this.formObject.value.deliveryMethod).forEach(key => {
      if (this.formObject.value.deliveryMethod[key]) {
        newFilterCount++;
      }
    });

    // Only process seller filters if the seller form group exists
    if (this.formObject.value.seller) {
      Object.keys(this.formObject.value.seller).forEach(key => {
        if (this.formObject.value.seller[key]) {
          newFilterCount++;
        }
      });
    }

    this.deliveryDateRange =
      this.formObject.value.deliveryDate.start +
      ' - ' +
      this.formObject.value.deliveryDate.end;

    if (
      this.deliveryDateRange === 'null - null' ||
      this.deliveryDateRange === ' - '
    ) {
      this.deliveryDateRange = 'Select a date range';
    }

    this.numberOfSelectedFilters = newFilterCount;
  }

  createForm(dateFilter: any = null) {
    const startDate = dateFilter?.deliveryDate?.start
      ? dateFilter.deliveryDate.start
      : '';
    const endDate = dateFilter?.deliveryDate?.end
      ? dateFilter.deliveryDate.end
      : '';

    // Check if seller filter feature is enabled
    const sellerFeatureEnabled =
      FEATURES.split_dynamic_order_status_seller_filter.control;

    // Create form without seller controls by default
    this.formObject = this.formBuilder.group({
      customers: this.formBuilder.group({}),
      orderStatus: this.formBuilder.group({}),
      deliveryDate: this.formBuilder.group({
        start: this.formBuilder.control(startDate),
        end: this.formBuilder.control(endDate),
        preset: this.formBuilder.control(null),
      }),
      deliveryMethod: this.formBuilder.group({}),
      rememberOrderStatus: this.formBuilder.control(false),
      rememberCustomers: this.formBuilder.control(false),
      rememberDeliveryMethod: this.formBuilder.control(false),
      rememberDeliveryDatePreset: this.formBuilder.control(false),
    });

    // Only add seller form group and control if the feature is enabled
    if (sellerFeatureEnabled) {
      this.formObject.addControl('seller', this.formBuilder.group({}));
      this.formObject.addControl(
        'rememberSeller',
        this.formBuilder.control(false),
      );
      // Disable the rememberSeller control
      this.formObject.get('rememberSeller').disable();
    }

    this.createCustomersForm();
    this.statusFilters().forEach((value, key) => {
      (this.formObject.get('orderStatus') as UntypedFormGroup).addControl(
        `${key}`,
        new UntypedFormControl(false),
      );
    });
    this.deliveryMethodFilters().forEach((value, key) => {
      (this.formObject.get('deliveryMethod') as UntypedFormGroup).addControl(
        `${key}`,
        new UntypedFormControl(false),
      );
    });

    // Only add seller controls if the feature is enabled
    if (sellerFeatureEnabled) {
      this.sellerFilters().forEach((value, key) => {
        (this.formObject.get('seller') as UntypedFormGroup).addControl(
          `${key}`,
          new UntypedFormControl(false),
        );
      });
    }
  }

  createCustomersForm() {
    this.customers?.forEach(c => {
      if (c.departments && c.departments.length > 0) {
        (this.formObject.get('customers') as UntypedFormGroup).addControl(
          `${c.customerNumber}`,
          new UntypedFormGroup({}),
        );
        c.departments.forEach(d => {
          (
            (this.formObject.get('customers') as UntypedFormGroup).get(
              `${c.customerNumber}`,
            ) as UntypedFormGroup
          ).addControl(`${d.departmentNumber}`, new UntypedFormControl(false));
        });
      } else {
        (this.formObject.get('customers') as UntypedFormGroup).addControl(
          `${c.customerNumber}`,
          new UntypedFormControl(false),
        );
      }
    });
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  goToTop() {
    if (
      this.platformService?.platformType ===
      this.platformService.platformEnum.desktop
    ) {
      this.virtualScroll?.scrollToIndex(0);
    } else if (
      this.platformService?.platformType ===
      this.platformService.platformEnum.tablet
    ) {
      const viewport = this.tabletVirtualScroll || this.virtualScroll;
      viewport?.scrollToIndex(0, 'smooth');
    } else {
      // Use mobileVirtualScroll for mobile
      const viewport = this.mobileVirtualScroll || this.virtualScroll;
      viewport?.scrollToIndex(0, 'smooth');
    }
  }

  handleRefresh(event: Event) {
    if (!this.orderService.networkStatus) {
      (event?.target as any)?.complete();
    } else {
      if (this.userService.isInternalUser && !this.orderReviewLoading) {
        this.orderReviewStoreService.getOrderReviewPreferences();
      }

      this.myOrdersStoreService.retrieveRecentOrders();
      this.myOrdersStoreService
        .state$()
        .pipe(
          filter((state: OrderState) => !state?.recentOrdersLoading),
          take(1),
        )
        .subscribe(() => {
          (event?.target as any)?.complete();
          this.reloadList();
        });
    }
  }

  showDownloadOrdersModal(isOpen: boolean) {
    this.downloadOrdersModalIsOpen$.next(isOpen);
  }

  showDownloadOrdersActionSheet(isOpen: boolean) {
    this.downloadOrdersActionSheetIsOpen$.next(isOpen);
  }

  handleDownloadOrders(fileInfo: DownloadOrdersEvent) {
    this.orderService.downloadFromOrderStatus(fileInfo);
  }

  enableRefresher() {
    this.viewPort?.elementScrolled()?.subscribe(scrollEvent => {
      const scrollY = this.viewPort.measureScrollOffset('top');
      this.hideRefresher = scrollY !== 0;
    });
  }

  originalFilters(filters) {
    return (
      filters?.filterSelected || Object.keys(filters?.filters || {}).length > 0
    );
  }

  // Ensure both desktop and mobile viewports are properly initialized
  private ensureViewportsInitialized() {
    if (this.viewPort) {
      this.viewPort.checkViewportSize();
    }

    if (this.virtualScroll) {
      this.virtualScroll.checkViewportSize();
    }

    if (this.mobileVirtualScroll) {
      this.mobileVirtualScroll.checkViewportSize();
    }

    if (this.tabletVirtualScroll) {
      this.tabletVirtualScroll.checkViewportSize();
    }

    // Force change detection to update views
    this.cdRef.detectChanges();
  }
}
